import { requestAssetApi } from './getAssetById';
import type { GeneralInfoPicklistItem } from './generalInfoApi';
import { normalizeAssetListResponse } from '../utils/assetListNormalize';
import { isAssetKeywordQueryActive } from '../utils/assetKeywordSearch';
import { assetRecordPrimaryId } from '../utils/assetRecordPrimaryId';
import type { AssistantPriceConstraint } from '../assistant/assistantPriceConstraint';
import {
  filterAssetRecordsForUiState,
  hasClientSideBrowseFilters,
} from '../utils/assetSearchRecordFilters';
import {
  refineAmenitySearchWithHydration,
  refineOpen24hSearchWithHydration,
  refineOpenWeekdaySearchWithHydration,
} from './assetOpen24hHydrate';
import type { FacilityIconId } from '../utils/assetFacilityIcons';
import {
  isAssistantOpenWeekdayFilter,
  type AssistantSchedule,
} from '../assistant/assistantSchedule';

/** API list-mode page size (search with `fetch_all` may return up to ~500). */
export const ASSET_SEARCH_PAGE_SIZE = 100;

/**
 * Max pages when aggregating for client-side browse filters that are not sent on the wire
 * (e.g. multi-type gym carousels). Keyword search no longer uses this crawl.
 */
export const ASSET_SEARCH_MAX_PAGES = 12;

/** First pages shown for non-keyword client-filter aggregation before background expand. */
export const ASSET_SEARCH_STAGED_FIRST_PAGES = 2;

/** Parallel list-mode requests while filling the rest of the active catalog. */
export const ASSET_SEARCH_CATALOG_EXPAND_CONCURRENCY = 3;

/** @deprecated Keyword search uses a single ES call — kept for callers/tests of catalog helpers. */
export const ASSET_SEARCH_KEYWORD_MAX_PAGES = 8;

/** How long to reuse the active-assets dump across non-keyword client filter remounts. */
export const ASSET_SEARCH_CATALOG_TTL_MS = 90_000;

/** Recommended debounce for free-text search keystrokes. */
export const ASSET_SEARCH_KEYWORD_DEBOUNCE_MS = 250;

/** Wire `sort` values accepted by `POST /search/assets`. */
export type AssetSearchSort = 'distance' | 'reviews_count' | 'bookings_count';

export type AssetSearchLocation = {
  latitude: number;
  longitude: number;
};

export type AssetSearchFilters = {
  asset_status: string[];
  min_capacity: number;
};

/** Wire body for `POST /search/assets`. */
export type SearchAssetsDto = {
  fuzzy: boolean;
  text?: string;
  /** Search mode: one response with up to ~500 matches (backend). */
  fetch_all?: boolean;
  /** Server ranking — send explicitly per carousel / browse intent. */
  sort?: AssetSearchSort;
  /**
   * Viewer geo for distance sort. Nested object only — do not put root `latitude` /
   * `longitude` on search bodies (those are asset-entity fields).
   */
  location?: AssetSearchLocation;
  filters: AssetSearchFilters;
  pagination: {
    from: number;
    size: number;
  };
};

/** UI-facing filter state mapped into {@link SearchAssetsDto}. */
export type AssetSearchUiState = {
  countryId?: number | null;
  stateId?: number | null;
  assetTypeId?: number | null;
  assetTypeIds?: number[] | null;
  text?: string;
  /** Hebrew locality from assistant / browse; applied client-side after the catalog dump. */
  city?: string | null;
  /** Catalog or spoken street; applied client-side so a leftover street is not dropped. */
  street?: string | null;
  /** Spoken “open 24 hours” — keep only listings with the 24/7 access facility. */
  open24h?: boolean;
  /** Facility catalog ids for the 24/7 access amenity. */
  open24hFacilityIds?: number[] | null;
  /** Spoken amenities (kitchen / wifi / parking) — hard facility-icon refine after hydrate. */
  amenityIcons?: FacilityIconId[] | null;
  /** Optional GET `/assets/facilities` rows to resolve id-only facility snapshots. */
  facilityCatalog?: Record<string, unknown>[] | null;
  /** “שפתוח בשבת” / weekly slot — open-weekday refine after opening_times hydrate. */
  schedule?: AssistantSchedule | null;
  /** Spoken hourly/daily price: less / more / equals. */
  price?: AssistantPriceConstraint | null;
  /** Backend assistant-turn ids; prefer these rows when the dump contains them. */
  assetIds?: string[] | null;
  /** Client-side label fallbacks when list rows omit numeric ids. */
  countryLabelLower?: string | null;
  stateLabelLower?: string | null;
  assetTypeLabelLower?: string | null;
  /** Multi-type browse: gym carousel vs spoken office family. */
  assetTypeFamily?: 'gym' | 'office' | null;
  assetTypesCatalog?: GeneralInfoPicklistItem[];
  /** Viewer coordinates → wire `location` when both are finite. */
  latitude?: number | null;
  longitude?: number | null;
  radiusKm?: number | null;
  sort?: AssetSearchSort | null;
  /** When true, request search-mode dump (`fetch_all`) even without keyword text. */
  fetchAll?: boolean | null;
};

export type AssetSearchResult = {
  records: Record<string, unknown>[];
  total: number;
  from: number;
  size: number;
  hasMore: boolean;
  /** True when this result came from wire `text` + `fetch_all` (skip client fuzzy refine). */
  serverTextSearch?: boolean;
  /** Background catalog pages are still arriving; filtered totals may grow. */
  catalogExpanding?: boolean;
};

type ActiveCatalogCache = {
  records: Record<string, unknown>[];
  serverTotal: number;
  /** Next list-mode `pagination.from` (not necessarily `records.length` after dedupe). */
  nextFrom: number;
  complete: boolean;
  fetchedAt: number;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function baseActiveFilters(): AssetSearchFilters {
  return {
    asset_status: ['active'],
    min_capacity: 0,
  };
}

/** True when browse needs aggregated fetch (client geo/type filters) or keyword search. */
export function hasFilterBeyondCountry(state: AssetSearchUiState): boolean {
  if (hasClientSideBrowseFilters(state)) {
    return true;
  }
  if (state.text?.trim()) {
    return true;
  }
  return false;
}

export { hasClientSideBrowseFilters };

function wireSortFromState(sort: AssetSearchUiState['sort']): AssetSearchSort | undefined {
  if (sort === 'distance' || sort === 'reviews_count' || sort === 'bookings_count') {
    return sort;
  }
  return undefined;
}

function wireLocationFromState(state: AssetSearchUiState): AssetSearchLocation | undefined {
  const lat = finiteNumber(state.latitude);
  const lng = finiteNumber(state.longitude);
  if (lat === null || lng === null) {
    return undefined;
  }
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return undefined;
  }
  return { latitude: lat, longitude: lng };
}

/**
 * Build the wire body for `POST /search/assets`.
 * - Keyword UI search uses the active catalog + client refine (see {@link fetchSearchAssetsForUiState}).
 * - This helper still supports Elasticsearch search-mode DTOs (`text` + `fetch_all`) for callers that need them.
 * - Home / ranked carousels send `sort` + nested `location` when geolocation is ready.
 * - Otherwise → list mode: paged `pagination.size` (max 100).
 */
export function buildSearchAssetsDto(
  state: AssetSearchUiState,
  options?: {
    from?: number;
    size?: number;
  },
): SearchAssetsDto {
  const text = typeof state.text === 'string' ? state.text.trim() : '';
  const keyword = isAssetKeywordQueryActive(text);
  const sort = wireSortFromState(state.sort);
  const location = wireLocationFromState(state);
  const fetchAll = keyword || state.fetchAll === true;

  if (keyword || fetchAll) {
    const dto: SearchAssetsDto = {
      fuzzy: true,
      filters: baseActiveFilters(),
      pagination: {
        from: 0,
        // Backend: size 0 with fetch_all returns the full search-mode match set.
        size: keyword ? 0 : Math.max(0, options?.size ?? 0),
      },
      fetch_all: true,
    };
    if (keyword) {
      dto.text = text;
    }
    if (sort) {
      dto.sort = sort;
    }
    if (location) {
      dto.location = location;
    }
    return dto;
  }

  const dto: SearchAssetsDto = {
    fuzzy: true,
    filters: baseActiveFilters(),
    pagination: {
      from: options?.from ?? 0,
      size: options?.size ?? ASSET_SEARCH_PAGE_SIZE,
    },
  };
  if (sort) {
    dto.sort = sort;
  }
  if (location) {
    dto.location = location;
  }
  return dto;
}

/** Keep search-mode fields plus server `sort` / nested `location`. */
export function sanitizeSearchAssetsWireBody(dto: SearchAssetsDto): SearchAssetsDto {
  const fetchAll = dto.fetch_all === true;
  const text = typeof dto.text === 'string' ? dto.text.trim() : '';
  const rawSize =
    typeof dto.pagination?.size === 'number' && Number.isFinite(dto.pagination.size)
      ? Math.floor(dto.pagination.size)
      : ASSET_SEARCH_PAGE_SIZE;
  const size = fetchAll
    ? Math.max(0, rawSize)
    : Math.min(ASSET_SEARCH_PAGE_SIZE, Math.max(1, rawSize));

  const out: SearchAssetsDto = {
    fuzzy: dto.fuzzy === true,
    filters: {
      asset_status: Array.isArray(dto.filters?.asset_status)
        ? dto.filters.asset_status.filter((s): s is string => typeof s === 'string')
        : ['active'],
      min_capacity:
        typeof dto.filters?.min_capacity === 'number' && Number.isFinite(dto.filters.min_capacity)
          ? dto.filters.min_capacity
          : 0,
    },
    pagination: {
      from:
        typeof dto.pagination?.from === 'number' && Number.isFinite(dto.pagination.from)
          ? Math.max(0, Math.floor(dto.pagination.from))
          : 0,
      size,
    },
  };
  if (text) {
    out.text = text;
  }
  if (fetchAll) {
    out.fetch_all = true;
  }
  const sort = wireSortFromState(dto.sort ?? null);
  if (sort) {
    out.sort = sort;
  }
  const loc = dto.location;
  if (loc && typeof loc === 'object') {
    const latitude = finiteNumber(loc.latitude);
    const longitude = finiteNumber(loc.longitude);
    if (
      latitude !== null &&
      longitude !== null &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    ) {
      out.location = { latitude, longitude };
    }
  }
  return out;
}

export function parseAssetSearchResponse(
  data: unknown,
  pagination?: { from: number; size: number },
  options?: { serverTextSearch?: boolean },
): AssetSearchResult {
  const records = normalizeAssetListResponse(data);
  const from = pagination?.from ?? 0;
  const size = pagination?.size ?? ASSET_SEARCH_PAGE_SIZE;
  let total = records.length;

  if (isPlainObject(data) && isPlainObject(data.hits)) {
    const rawTotal = data.hits.total;
    if (typeof rawTotal === 'number' && Number.isFinite(rawTotal)) {
      total = rawTotal;
    } else if (isPlainObject(rawTotal) && typeof rawTotal.value === 'number') {
      total = rawTotal.value;
    }
  } else if (typeof data === 'object' && data !== null) {
    const root = data as Record<string, unknown>;
    for (const key of ['total', 'totalCount', 'count'] as const) {
      const n = finiteNumber(root[key]);
      if (n !== null) {
        total = n;
        break;
      }
    }
  }

  const serverTextSearch = options?.serverTextSearch === true;
  const loadedThrough = from + records.length;
  const hasMore = serverTextSearch
    ? false
    : total > loadedThrough ||
      (size > 0 && records.length >= size && total <= loadedThrough && records.length > 0);

  return {
    records,
    total,
    from,
    size: serverTextSearch ? records.length : size,
    hasMore,
    serverTextSearch,
  };
}

export async function postSearchAssets(dto: SearchAssetsDto): Promise<unknown> {
  return requestAssetApi({
    method: 'POST',
    path: '/search/assets',
    body: sanitizeSearchAssetsWireBody(dto),
  });
}

async function fetchSearchAssetsPageRaw(
  state: AssetSearchUiState,
  from = 0,
  size = ASSET_SEARCH_PAGE_SIZE,
): Promise<AssetSearchResult> {
  const dto = buildSearchAssetsDto(state, { from, size });
  const raw = await postSearchAssets(dto);
  const keyword = isAssetKeywordQueryActive(typeof state.text === 'string' ? state.text : '');
  return parseAssetSearchResponse(
    raw,
    { from: dto.pagination.from, size: dto.pagination.size },
    { serverTextSearch: keyword },
  );
}

function dedupeAssetRecordsById(records: Record<string, unknown>[]): Record<string, unknown>[] {
  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];
  for (const row of records) {
    const key = assetRecordPrimaryId(row);
    if (!key) {
      out.push(row);
      continue;
    }
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(row);
  }
  return out;
}

function applyClientBrowseFiltersIfNeeded(
  records: Record<string, unknown>[],
  state: AssetSearchUiState,
): Record<string, unknown>[] {
  if (!hasClientSideBrowseFilters(state)) {
    return records;
  }
  return filterAssetRecordsForUiState(records, state);
}

function browseNeedsFacilityHydration(state: AssetSearchUiState): boolean {
  return (
    Boolean(state.open24h) ||
    Boolean(state.amenityIcons && state.amenityIcons.length > 0) ||
    isAssistantOpenWeekdayFilter(state.schedule)
  );
}

async function applyClientBrowseFiltersForUiState(
  records: Record<string, unknown>[],
  state: AssetSearchUiState,
): Promise<Record<string, unknown>[]> {
  if (!hasClientSideBrowseFilters(state)) {
    return records;
  }
  if (!browseNeedsFacilityHydration(state)) {
    return filterAssetRecordsForUiState(records, state);
  }
  const withoutHydratedFilters: AssetSearchUiState = {
    ...state,
    open24h: false,
    amenityIcons: null,
    schedule: null,
  };
  let base = filterAssetRecordsForUiState(records, withoutHydratedFilters);
  const catalog = state.facilityCatalog ?? [];
  if (state.open24h) {
    base = await refineOpen24hSearchWithHydration(base, state.open24hFacilityIds ?? [], catalog);
  }
  if (state.amenityIcons && state.amenityIcons.length > 0) {
    base = await refineAmenitySearchWithHydration(base, state.amenityIcons, catalog);
  }
  if (isAssistantOpenWeekdayFilter(state.schedule)) {
    base = await refineOpenWeekdaySearchWithHydration(base, state.schedule);
  }
  return base;
}

/** Fetch one search page (server results only — apply browse filters in the UI when needed). */
export async function fetchSearchAssetsPage(
  state: AssetSearchUiState,
  from = 0,
  size = ASSET_SEARCH_PAGE_SIZE,
): Promise<AssetSearchResult> {
  const page = await fetchSearchAssetsPageRaw(state, from, size);
  if (page.serverTextSearch && hasClientSideBrowseFilters(state)) {
    const filtered = await applyClientBrowseFiltersForUiState(page.records, state);
    return {
      ...page,
      records: filtered,
      total: filtered.length,
      size: filtered.length,
    };
  }
  return page;
}

let activeCatalogCache: ActiveCatalogCache | null = null;
let catalogExpandPromise: Promise<void> | null = null;
let catalogExpandGeneration = 0;
let catalogOnPartial: ((result: AssetSearchResult) => void) | undefined;

function isActiveCatalogFresh(cache: ActiveCatalogCache): boolean {
  return Date.now() - cache.fetchedAt < ASSET_SEARCH_CATALOG_TTL_MS;
}

function catalogResultFromCache(cache: ActiveCatalogCache): AssetSearchResult {
  return {
    records: cache.records,
    total: cache.serverTotal || cache.records.length,
    from: 0,
    size: cache.records.length,
    hasMore: false,
    catalogExpanding: !cache.complete,
  };
}

function maxCatalogRecords(maxPages: number, pageSize: number): number {
  return Math.max(pageSize, maxPages * pageSize);
}

function emitCatalogPartial(): void {
  if (!activeCatalogCache) {
    return;
  }
  catalogOnPartial?.(catalogResultFromCache(activeCatalogCache));
}

function startCatalogExpand(
  maxPages: number,
  pageSize: number,
  onPartial?: (result: AssetSearchResult) => void,
): void {
  if (onPartial) {
    catalogOnPartial = onPartial;
  }
  const cache = activeCatalogCache;
  if (!cache || cache.complete || catalogExpandPromise) {
    return;
  }
  catalogExpandPromise = expandActiveCatalogTo(maxPages, pageSize).finally(() => {
    catalogExpandPromise = null;
  });
}

/** Drop cached active catalog (manual refresh / tests). */
export function invalidateActiveAssetCatalogCache(): void {
  catalogExpandGeneration += 1;
  activeCatalogCache = null;
  catalogExpandPromise = null;
  catalogOnPartial = undefined;
}

/** @deprecated Prefer {@link invalidateActiveAssetCatalogCache}. */
export function clearActiveAssetCatalogCacheForTests(): void {
  invalidateActiveAssetCatalogCache();
}

/**
 * Reuse a dump already in memory (home `fetch_all` pool) so browse filters do not
 * start from an empty first page.
 */
export function seedActiveAssetCatalogCache(
  records: Record<string, unknown>[],
  serverTotal?: number,
): void {
  if (records.length === 0) {
    return;
  }
  const existing = activeCatalogCache;
  if (
    existing &&
    isActiveCatalogFresh(existing) &&
    existing.records.length >= records.length
  ) {
    if (!existing.complete) {
      startCatalogExpand(ASSET_SEARCH_MAX_PAGES, ASSET_SEARCH_PAGE_SIZE);
    }
    return;
  }

  const total = Math.max(serverTotal ?? 0, records.length);
  const nextFrom = records.length;
  const complete = total > 0 && nextFrom >= total;
  catalogExpandGeneration += 1;
  catalogExpandPromise = null;
  activeCatalogCache = {
    records: dedupeAssetRecordsById(records),
    serverTotal: total,
    nextFrom,
    complete,
    fetchedAt: Date.now(),
  };
  if (!complete) {
    startCatalogExpand(ASSET_SEARCH_MAX_PAGES, ASSET_SEARCH_PAGE_SIZE);
  }
}

async function tryFetchAllActiveDump(): Promise<AssetSearchResult | null> {
  try {
    const dump = await fetchSearchAssetsPageRaw({ fetchAll: true }, 0, 0);
    return dump.records.length > 0 ? dump : null;
  } catch {
    return null;
  }
}

async function expandActiveCatalogTo(maxPages: number, pageSize: number): Promise<void> {
  const generation = catalogExpandGeneration;
  const cap = maxCatalogRecords(maxPages, pageSize);
  while (true) {
    if (generation !== catalogExpandGeneration) {
      return;
    }
    const cache = activeCatalogCache;
    if (!cache || !isActiveCatalogFresh(cache) || cache.complete) {
      return;
    }
    if (cache.nextFrom >= cap || (cache.serverTotal > 0 && cache.nextFrom >= cache.serverTotal)) {
      cache.complete = true;
      emitCatalogPartial();
      return;
    }

    const batchFrom: number[] = [];
    let from = cache.nextFrom;
    while (
      batchFrom.length < ASSET_SEARCH_CATALOG_EXPAND_CONCURRENCY &&
      from < cap &&
      (cache.serverTotal <= 0 || from < cache.serverTotal)
    ) {
      batchFrom.push(from);
      from += pageSize;
    }
    if (batchFrom.length === 0) {
      cache.complete = true;
      emitCatalogPartial();
      return;
    }

    const pages = await Promise.all(
      batchFrom.map((offset) => fetchSearchAssetsPageRaw({}, offset, pageSize)),
    );
    if (generation !== catalogExpandGeneration) {
      return;
    }

    let nextRecords = cache.records;
    let nextFrom = cache.nextFrom;
    let serverTotal = cache.serverTotal;
    let exhausted = false;
    for (let i = 0; i < pages.length; i += 1) {
      const page = pages[i];
      serverTotal = Math.max(serverTotal, page.total);
      nextRecords = dedupeAssetRecordsById([...nextRecords, ...page.records]);
      nextFrom = batchFrom[i] + pageSize;
      if (!page.hasMore || page.records.length === 0) {
        exhausted = true;
        break;
      }
    }

    const complete = exhausted || nextFrom >= cap || (serverTotal > 0 && nextFrom >= serverTotal);
    activeCatalogCache = {
      records: nextRecords,
      serverTotal,
      nextFrom,
      complete,
      fetchedAt: cache.fetchedAt,
    };
    emitCatalogPartial();
    if (complete) {
      return;
    }
  }
}

/**
 * Load (or reuse) the active-assets dump used for client-side browse filters and
 * keyword substring matching (≥3 characters).
 */
export async function fetchActiveAssetCatalogStaged(options: {
  firstPages?: number;
  maxPages?: number;
  onPartial?: (result: AssetSearchResult) => void;
}): Promise<AssetSearchResult> {
  const pageSize = ASSET_SEARCH_PAGE_SIZE;
  const firstPages = Math.max(1, options.firstPages ?? ASSET_SEARCH_STAGED_FIRST_PAGES);
  const maxPages = Math.max(firstPages, options.maxPages ?? ASSET_SEARCH_MAX_PAGES);
  const onPartial = options.onPartial;
  if (onPartial) {
    catalogOnPartial = onPartial;
  }

  const existing = activeCatalogCache;
  if (existing && isActiveCatalogFresh(existing) && existing.records.length > 0) {
    onPartial?.(catalogResultFromCache(existing));
    if (!existing.complete) {
      startCatalogExpand(maxPages, pageSize, onPartial);
    }
    return catalogResultFromCache(existing);
  }

  catalogExpandGeneration += 1;
  activeCatalogCache = null;
  catalogExpandPromise = null;

  const dump = await tryFetchAllActiveDump();
  if (dump) {
    const nextFrom = dump.records.length;
    const complete =
      !dump.hasMore || nextFrom >= maxCatalogRecords(maxPages, pageSize) || nextFrom >= dump.total;
    activeCatalogCache = {
      records: dedupeAssetRecordsById(dump.records),
      serverTotal: Math.max(dump.total, dump.records.length),
      nextFrom,
      complete,
      fetchedAt: Date.now(),
    };
    const first = catalogResultFromCache(activeCatalogCache);
    onPartial?.(first);
    if (!complete) {
      startCatalogExpand(maxPages, pageSize, onPartial);
    }
    return first;
  }

  const firstPage = await fetchSearchAssetsPageRaw({}, 0, pageSize);
  let combined = dedupeAssetRecordsById(firstPage.records);
  let total = firstPage.total;
  let nextFrom = pageSize;
  let hasMore = firstPage.hasMore && firstPage.records.length > 0;

  if (hasMore && firstPages > 1) {
    const extraFrom = Array.from({ length: firstPages - 1 }, (_, i) => (i + 1) * pageSize);
    const extras = await Promise.all(
      extraFrom.map((offset) => fetchSearchAssetsPageRaw({}, offset, pageSize)),
    );
    for (let i = 0; i < extras.length; i += 1) {
      const page = extras[i];
      total = Math.max(total, page.total);
      combined = dedupeAssetRecordsById([...combined, ...page.records]);
      nextFrom = extraFrom[i] + pageSize;
      hasMore = page.hasMore && page.records.length > 0;
      if (!hasMore) {
        break;
      }
    }
  }

  const actuallyComplete = !hasMore || nextFrom >= maxCatalogRecords(maxPages, pageSize);
  activeCatalogCache = {
    records: combined,
    serverTotal: total,
    nextFrom,
    complete: actuallyComplete,
    fetchedAt: Date.now(),
  };
  const first = catalogResultFromCache(activeCatalogCache);
  onPartial?.(first);
  if (!actuallyComplete) {
    startCatalogExpand(maxPages, pageSize, onPartial);
  }
  return first;
}

export type FetchSearchAssetsForUiStateOptions = {
  from?: number;
  size?: number;
  /** Called when staged catalog pages arrive (non-keyword client-filter modes only). */
  onPartial?: (result: AssetSearchResult) => void;
  /** Bypass and rebuild the active catalog cache (browse-filter path). */
  forceRefresh?: boolean;
};

/**
 * Keyword (≥3 chars) → active catalog + client exact/substring refine (see useBookableAssetSearch).
 * Client browse filters without keyword → staged catalog + local filter.
 * Plain browse → one list-mode page.
 *
 * Note: Elasticsearch `text` search alone often misses name prefixes like `ono` → `onobiz`.
 */
export async function fetchSearchAssetsForUiState(
  state: AssetSearchUiState,
  options?: FetchSearchAssetsForUiStateOptions,
): Promise<AssetSearchResult> {
  const from = options?.from ?? 0;
  const size = options?.size ?? ASSET_SEARCH_PAGE_SIZE;

  if (from > 0 && !isAssetKeywordQueryActive(state.text ?? '')) {
    return fetchSearchAssetsPage(state, from, size);
  }

  if (options?.forceRefresh) {
    invalidateActiveAssetCatalogCache();
  }

  if (isAssetKeywordQueryActive(state.text ?? '') || hasClientSideBrowseFilters(state)) {
    const result = await fetchActiveAssetCatalogStaged({
      firstPages: ASSET_SEARCH_STAGED_FIRST_PAGES,
      maxPages: ASSET_SEARCH_MAX_PAGES,
      onPartial:
        options?.onPartial && !browseNeedsFacilityHydration(state)
          ? (partial) => {
              const filtered = applyClientBrowseFiltersIfNeeded(partial.records, state);
              options.onPartial?.({
                ...partial,
                records: filtered,
                total: filtered.length,
                size: filtered.length,
                // Keyword refine runs in the hook (needs i18n language).
                serverTextSearch: false,
              });
            }
          : undefined,
    });
    const apply = async (base: AssetSearchResult): Promise<AssetSearchResult> => {
      const filtered = await applyClientBrowseFiltersForUiState(base.records, state);
      return {
        ...base,
        records: filtered,
        total: filtered.length,
        size: filtered.length,
        serverTextSearch: false,
        hasMore: false,
        catalogExpanding: Boolean(base.catalogExpanding),
      };
    };
    if ((!options?.onPartial || browseNeedsFacilityHydration(state)) && catalogExpandPromise) {
      await catalogExpandPromise;
      return apply(activeCatalogCache ? catalogResultFromCache(activeCatalogCache) : result);
    }
    return apply(result);
  }

  return fetchSearchAssetsPage(state, from, size);
}

/**
 * Keyword → full active catalog (client refine in callers).
 * Otherwise staged active catalog / browse filters.
 */
export async function fetchAllSearchAssetPages(
  state: AssetSearchUiState,
  pageSize = ASSET_SEARCH_PAGE_SIZE,
): Promise<AssetSearchResult> {
  void pageSize;
  const result = await fetchActiveAssetCatalogStaged({
    firstPages: ASSET_SEARCH_STAGED_FIRST_PAGES,
    maxPages: ASSET_SEARCH_MAX_PAGES,
  });
  if (catalogExpandPromise) {
    await catalogExpandPromise;
  }
  const base = activeCatalogCache ? catalogResultFromCache(activeCatalogCache) : result;
  if (hasClientSideBrowseFilters(state)) {
    const filtered = await applyClientBrowseFiltersForUiState(base.records, state);
    return {
      ...base,
      records: filtered,
      total: filtered.length,
      size: filtered.length,
      serverTextSearch: false,
      hasMore: false,
      catalogExpanding: false,
    };
  }
  if (isAssetKeywordQueryActive(state.text ?? '')) {
    return { ...base, serverTextSearch: false, hasMore: false };
  }
  return base;
}

/** Default list-mode first page (country-only or unfiltered browse). */
export async function fetchDefaultBookableAssetListPage(from = 0): Promise<AssetSearchResult> {
  return fetchSearchAssetsPage({}, from, ASSET_SEARCH_PAGE_SIZE);
}
